// Resolve stored Learning Center asset URLs (images / attachments) for display.
//
// Module assets live in our PRIVATE documents bucket (documents[-test].hopdrive.com), so a
// stored permanent URL 403s if opened directly — it only works via a short-lived presigned
// GET URL from the requestDownloadUrl function. Ported from admin-portal-v5's upload.ts
// (download half only; the dealer portal never uploads).

import { authService } from '@features/auth/services/authService';
import { DOCUMENTS_BUCKET_URL_PREFIX } from './documentsBucket';

// Matches our private documents buckets. Stored URLs come in two shapes: the bucket domain as
// the host, or path-style S3 (https://s3[.region].amazonaws.com/<bucket>/<key>). Anchored to
// those two hosts so an arbitrary URL merely *containing* the bucket domain in its path
// (e.g. https://evil.com/documents.hopdrive.com/x) isn't classified as a private asset. The host
// shape is shared with useResolvedMarkdown's scanner via DOCUMENTS_BUCKET_URL_PREFIX; here it is
// anchored and followed by the object key. match[1] = bucket host, match[2] = key.
const DOCUMENTS_BUCKET_RE = new RegExp(`^${DOCUMENTS_BUCKET_URL_PREFIX}\\/(.+)$`, 'i');

/**
 * Whether a URL points at one of our private documents buckets. Such URLs 403 if opened
 * directly, so callers that can't resolve a presigned URL must not surface them as plain links.
 */
export function isPrivateAssetUrl(url: string | null | undefined): boolean {
  return !!url && DOCUMENTS_BUCKET_RE.test(url);
}

/**
 * Resolve a stored asset URL to something the browser can load. Private documents-bucket URLs
 * are exchanged for a short-lived presigned GET (via /.netlify/functions/requestDownloadUrl);
 * any other URL (public link, YouTube thumbnail, placeholder) is returned unchanged.
 *
 * Presigned URLs expire (~5 min), so resolve at display/click time, not once up front.
 */
export async function resolveAssetUrl(url: string | null | undefined): Promise<string> {
  if (!url) return '';
  const match = url.match(DOCUMENTS_BUCKET_RE);
  if (!match) {
    // External/public URL — use as-is, but only if it's an http(s) (or same-origin relative) URL.
    // attachment_url is free text an author types, so a `javascript:`/`data:` scheme here would be
    // navigated to when a viewer clicks Download; reject anything else.
    try {
      const { protocol } = new URL(url, window.location.href);
      return protocol === 'http:' || protocol === 'https:' ? url : '';
    } catch {
      return '';
    }
  }
  // The key was lifted from a URL path, so it may be percent-encoded; S3 wants the literal
  // object key (e.g. `a%20b` in the URL names an object whose key contains a real space).
  const rawKey = match[2].split('?')[0];
  let key: string;
  try {
    key = decodeURIComponent(rawKey);
  } catch {
    key = rawKey; // malformed escape sequence — pass through unchanged
  }

  const token = await authService.getAccessToken();
  const res = await fetch('/.netlify/functions/requestDownloadUrl', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ key }),
  });
  if (!res.ok) throw new Error(`Failed to resolve asset URL (${res.status})`);
  const data = await res.json();
  // Validate rather than trust the function's response shape: if requestDownloadUrl is ever
  // refactored to return something malformed, fail loudly here so callers hit their fallback
  // (gradient thumbnail / blocked-popup toast) instead of silently handing `undefined` or an
  // object to an <img src> or window.location.
  if (typeof data?.downloadURL !== 'string' || !data.downloadURL) {
    throw new Error('requestDownloadUrl returned an invalid download URL');
  }
  return data.downloadURL;
}
